import SyntheticEvent from './synthetic-event';
import PlaybackButton from './playback-button';
import NavButtons from './nav-buttons';
import Progress from './progress';
import Autoplay from './autoplay';

export const Status = {
    Idle: 'idle',
    Playing: 'playing',
};

class Gallery {
    /**
     * Create a new gallery.
     * @param {HTMLElement} galleryElement - Element that contains gallery HTML.
     * @param {Object} props
     * @param {number} props.interval      - Interval between frame updates.
     * @param {number} props.index         - Current visible frame.
     */
    constructor(galleryElement, props = {}) {
        this.dom = this.queryDOM(galleryElement);
        this.event = new SyntheticEvent('indexchange', 'statuschange', 'bufferchange');
        this.state = {
            interval: props.interval || 1000,
            index: props.index || 0,
            inactiveClassName: props.activeClassName || 'content--inactive',
            buffer: props.buffer || 5000,
            status: Status.Idle,
        };
        this.features = {};
        this.render();
        this.bufferContents(this.state.buffer);

        galleryElement.classList.remove('no-js');
    }

    /** Add a play/pause button. */
    withPlaybackButton() {
        this.features.playbackButton = new PlaybackButton(this);
        this.dom.playbackButton.classList.remove('hidden');
        return this;
    }

    /** Add previous and next buttons. */
    withNavButtons() {
        this.features.navButtons = new NavButtons(this);
        this.dom.prevButton.classList.remove('hidden');
        this.dom.nextButton.classList.remove('hidden');
        return this;
    }

    /** Add a progress bar. */
    withProgress() {
        this.features.progress = new Progress(this);
        this.dom.progressContainer.classList.remove('hidden');
        return this;
    }

    /**
     * Add autoplay feature. Play the gallery when it's in viewport and pause when it's out of view.
     * NOTE: When browser has no IntersectionObserver, autoplay only play/pause based on tab's visibility.
     */
    withAutoplay() {
        this.features.autoplay = new Autoplay(this);
        this.features.autoplay.start();
        return this;
    }

    /**
     * Query important elements from the gallery element.
     * @private
     * @param {HTMLElement} galleryElement - Element that contains gallery HTML.
     */
    queryDOM(galleryElement) {
        const contentContainer = galleryElement.querySelector('.content__container');
        const contents = Array.from(contentContainer.children);
        return {
            element: galleryElement,
            prevButton: galleryElement.querySelector('button[data-action="previous"]'),
            nextButton: galleryElement.querySelector('button[data-action="next"]'),
            playbackButton: galleryElement.querySelector('button[data-action="playback"]'),
            progressContainer: galleryElement.querySelector('.progress__container'),
            progress: galleryElement.querySelector('.progress'),
            bufferProgress: galleryElement.querySelector('.progress__buffer'),
            contents,
        };
    }

    /**
     * Set current index and render the gallery.
     * @param {number} index - New index. Will get clamped between 0 and contents' length.
     */
    async setIndex(index) {
        const idx = clamp(index, 0, this.length() - 1);
        const lastIndex = this.state.index;
        if (lastIndex === idx) {
            return;
        }
        this.state.index = idx;
        this.bufferContents(this.state.buffer);
        const checkBuffer = () => this.bufferProcess() >= idx / (this.length() - 1);
        await waitUntil(checkBuffer);
        this.render();
        this.event.dispatch('indexchange', idx, lastIndex);
    }

    async next() {
        await this.setIndex(this.state.index + 1);
    }

    async previous() {
        await this.setIndex(this.state.index - 1);
    }

    play() {
        if (this.state.status !== Status.Playing) {
            this.state.status = Status.Playing;
            this.event.dispatch('statuschange');
        }
        this.dom.element.setAttribute('data-status', this.state.status);
        const oninterval = async () => {
            const { index, status, interval } = this.state;

            await this.next();

            if (status === Status.Idle || index === this.length() - 1) {
                this.pause();
                this.render();
                this.event.dispatch('statuschange');
                window.clearTimeout(oninterval);
                return;
            }
            window.setTimeout(oninterval, interval);
        };
        window.setTimeout(oninterval, this.state.interval);
    }

    pause() {
        if (this.state.status !== Status.Idle) {
            this.state.status = Status.Idle;
            this.event.dispatch('statuschange');
        }
        this.dom.element.setAttribute('data-status', this.state.status);
    }

    /**
     * Load previous frame and next frames up until duration.
     * @private
     * @param {number} [duration=5000] - Duration to load beforehand (ms).
     */
    async bufferContents(duration = 5000) {
        const { index, interval } = this.state;
        const n = Math.max(duration / interval, 1);
        const begin = clamp(index - 1, 0, this.length() - 1);
        const end = clamp(index + n, 0, this.length());
        const contents = this.dom.contents.slice(begin, end);
        await loadOneByOne(contents[0], ...contents.slice(1));
        this.event.dispatch('bufferchange', this.bufferProcess());
    }

    addEventListener(event, ...args) {
        this.event.addEventListener(event, ...args);
    }

    /**
     * Render current frame.
     * @private
     */
    render() {
        const { index, status, inactiveClassName } = this.state;
        const { element, contents } = this.dom;
        element.setAttribute('data-status', status);
        contents.slice(0, index + 1).forEach(c => c.classList.remove(inactiveClassName));
        contents.slice(index + 1).forEach(c => c.classList.add(inactiveClassName));
    }

    length() {
        return this.dom.contents.length;
    }

    bufferProcess() {
        const loadings = this.dom.contents
            .filter(c => {
                const target = c.querySelector('[src]') || c.querySelector('[data-src]') || c;
                return target.src && target.complete;
            });
        return loadings.length / this.length();
    }
}

function clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
}

async function load(element) {
    const el = element.querySelector('[data-src]') || element;
    const src = el.getAttribute('data-src');
    if (el.src || !src) {
        return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
        const onload = () => {
            resolve();
            el.removeEventListener('load', onload);
        };
        const onerror = (err) => {
            reject(err);
            el.removeEventListener('error', onerror);
        };
        el.addEventListener('load', onload, false);
        el.addEventListener('error', onerror, false);
        el.src = src;
    });
}

async function loadOneByOne(element, ...elements) {
    if (elements.length === 0) {
        return load(element);
    }
    return load(element)
        .then(() => loadOneByOne(elements[0], ...elements.slice(1)))
        .catch(err => {
            console.error(err); // eslint-disable-line no-console
        });
}

async function waitUntil(conditionFn) {
    return new Promise((resolve) => {
        const check = () => {
            if (conditionFn()) {
                resolve();
                return;
            }
            window.setTimeout(check, 200);
        };
        window.setTimeout(check, 200);
    });
}

export default Gallery;
